import React, { useState, useEffect, useReducer } from 'react';
import { Notification } from 'common/notification';
import { Footer, FooterType } from 'common/footer';
import { goToPage, RouteName } from 'routes';
import { ButtonStyle } from 'common/button';
import { utmProperties } from 'config/constants';
import { UserController } from 'networking/controllers/user-controller';
import { SubscriptionController } from 'networking/controllers/subscripton-controller';
import { ReactComponent as LogoIntervivos } from 'assets/images/intervivosLogo.svg';
import {
  capitalizeFirstLetter,
  classnames,
  getQueryParam,
} from 'helpers/utils';
import { ModalWarning } from 'common/modal-warning';
import { trackCustomEvent } from 'helpers/analytics';
import { getRouteFor } from 'routes/route-helpers';
import { LocalStorageApi } from 'helpers/local-storage';
import { LegacyVideoModal } from 'common/legacy-video-modal/legacy-video-modal';
import { LegacyVideoSection } from 'common/legacy-video-section/legacy-video-section';
import { Breakpoints } from 'common/enums';
import { useMediaQuery } from 'hooks/use-media-query';
import { SignUpReducer, signUpinitialState } from './sign-up-reducer';
import { SignUpStep1 } from './sections';
import styles from './sign-up.module.scss';

const SignUp: React.FC = () => {
  const [state, dispatch] = useReducer(SignUpReducer, signUpinitialState);
  const [invalidCouponCode, setInvalidCouponCode] = useState<boolean>(false);

  const [showError, setShowError] = useState(false);
  const [isCompany, setIsCompany] = useState<boolean>(false);

  const [showVideo, setShowVideo] = useState(false);

  const token = getQueryParam('token') ?? undefined;
  const isMobileOrTablet = useMediaQuery(`(max-width: ${Breakpoints.md}px)`);

  const addCouponCode = async (couponCode: string) => {
    try {
      const response =
        await SubscriptionController.getSubscriptionDealByCode(couponCode);
      dispatch({
        type: 'UPDATE_SUBSCRIPTION_DEALS',
        subscriptionDeals: [response],
      });
      const couponCodeId = { id: response.id, code: couponCode };
      dispatch({ type: 'COUPON_CODE_APPLIED', couponCodeId: [couponCodeId] });
    } catch (error) {
      setInvalidCouponCode(true);
    }
  };

  useEffect(() => {
    trackCustomEvent('startSignUp');
  }, []);

  useEffect(() => {
    ['name', 'email'].forEach((queryParam: string) => {
      const contributorValue = getQueryParam(queryParam);

      if (contributorValue) {
        dispatch({
          type: 'CHANGE_INPUT',
          object: 'user',
          field: queryParam,
          value: contributorValue || '',
        });
      }
    });

    const companySingUp = getQueryParam('company');
    if (companySingUp) {
      dispatch({
        type: 'CHANGE_INPUT',
        object: 'user',
        field: 'companyName',
        value: capitalizeFirstLetter(companySingUp),
      });
      setIsCompany(true);
    }

    if (token) {
      dispatch({ type: 'ADD_TOKEN', token });
    }

    utmProperties.forEach((utmProperty: UtmProperties) => {
      const utmValue = getQueryParam(utmProperty);
      if (utmValue) {
        dispatch({
          type: 'SET_UTM_PROPERTY',
          utmProperty: UserController.deSerializeUtmProperty(
            utmProperty,
          ) as string,
          utmValue,
        });
      }
    });

    const couponCode = getQueryParam('couponCode');
    if (couponCode) {
      addCouponCode(couponCode);
    }

    const vaultIdParam = getQueryParam('vaultId');
    const tab = getQueryParam('tab');
    if (vaultIdParam && tab) {
      const vaultUrl = getRouteFor(
        RouteName.VaultDetail,
        {
          id: vaultIdParam,
        },
        { tab },
      );
      LocalStorageApi.set('previous-url', vaultUrl);
    }

    return () => {
      dispatch({ type: 'REGISTER_SUCCESS' });
    };
  }, []);

  const showSignUpStep1View = () => (
    <SignUpStep1
      user={state.user}
      dispatch={dispatch}
      isCompany={isCompany}
      couponsCodeApplied={state.couponCodeApplied}
      token={token}
      onClickPlayVideo={() => setShowVideo(true)}
    />
  );

  const showCompanyLogo = (company: string) => {
    if (company.toLowerCase() === 'intervivos') {
      return <LogoIntervivos />;
    }
    return null;
  };
  const showContentView = () => (
    <div className={styles.fullContent}>
      <div className={classnames(styles.marginTop)}>
        <div className={styles.elements}>
          {isCompany && (
            <>
              <p className="text__heading6__surfaceLight50"> & </p>
              {showCompanyLogo(state.user.companyName)}
            </>
          )}
        </div>
        {showSignUpStep1View()}
      </div>
    </div>
  );
  const showInvalidCodeModal = () => (
    <ModalWarning
      title="Invalid coupon code"
      content={
        <div className={styles.modalSignUpInfo}>
          There’s been an error with the coupon code associated with your
          referral link. Please contact support for more information.
        </div>
      }
      successButtonText="Register without coupon"
      closeFn={() => goToPage(RouteName.SignIn)}
      successFn={() => setInvalidCouponCode(false)}
      disableManualClosing
      successStyleButton={ButtonStyle.PrimaryFilled}
    />
  );

  const showErrorNotification = () => (
    <Notification handleClose={() => setShowError(false)} />
  );

  return (
    <div className={styles.container}>
      {showError && showErrorNotification()}
      {invalidCouponCode && showInvalidCodeModal()}
      {showVideo && <LegacyVideoModal onClose={() => setShowVideo(false)} />}
      {!isMobileOrTablet && (
        <LegacyVideoSection showVideo={() => setShowVideo(true)} />
      )}
      <div className={styles.contentRight}>
        <div className={styles.contentFormWrapper}>{showContentView()}</div>
        <div className={styles.contentFooter}>
          <Footer footerType={FooterType.NoSignedUser} />
        </div>
      </div>
    </div>
  );
};

export { SignUp };
